/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { workloadIdentitiesWithdrawSubject } from "../funcs/workloadIdentitiesWithdrawSubject.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { WorkloadIdentityPolicy } from "../models/components/workloadidentitypolicy.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  WithdrawWorkloadSubjectRequest,
  WithdrawWorkloadSubjectSecurity,
} from "../models/operations/withdrawworkloadsubject.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type WithdrawWorkloadSubjectMutationVariables = {
  request: WithdrawWorkloadSubjectRequest;
  security?: WithdrawWorkloadSubjectSecurity | undefined;
  options?: RequestOptions;
};

export type WithdrawWorkloadSubjectMutationData = WorkloadIdentityPolicy;

export type WithdrawWorkloadSubjectMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * withdrawSubject workloadIdentities
 *
 * @remarks
 * Withdraw an admitted subject and its agent assignment, stopping it authenticating. Requires workload:write.
 */
export function useWithdrawWorkloadSubjectMutation(
  options?: MutationHookOptions<
    WithdrawWorkloadSubjectMutationData,
    WithdrawWorkloadSubjectMutationError,
    WithdrawWorkloadSubjectMutationVariables
  >,
): UseMutationResult<
  WithdrawWorkloadSubjectMutationData,
  WithdrawWorkloadSubjectMutationError,
  WithdrawWorkloadSubjectMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildWithdrawWorkloadSubjectMutation(client, options),
    ...options,
  });
}

export function mutationKeyWithdrawWorkloadSubject(): MutationKey {
  return ["@gram/client", "workloadIdentities", "withdrawSubject"];
}

export function buildWithdrawWorkloadSubjectMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: WithdrawWorkloadSubjectMutationVariables,
  ) => Promise<WithdrawWorkloadSubjectMutationData>;
} {
  return {
    mutationKey: mutationKeyWithdrawWorkloadSubject(),
    mutationFn: function withdrawWorkloadSubjectMutationFn({
      request,
      security,
      options,
    }): Promise<WithdrawWorkloadSubjectMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(workloadIdentitiesWithdrawSubject(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
